import { Box, Link, VStack } from "@chakra-ui/react"
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { IKReactSelect } from '../../../ui-kit'
import type { SourcesResponse } from '../../interfaces'

interface Option {
  value: string
  label: string
}

interface Props {
  sourcesJson: {
    [sourceId: string]: SourcesResponse
  }
  selectedSources: string[]
  addOriginMenuOpen: boolean
  selectRefCallback(element: HTMLSelectElement): void
  closeAddOriginMenu(): void
  openAddOriginMenu(): void
  onCreateOriginClick(): void
  handleChange(option: Option | null): void
}

interface SelectOptionProps {
  value: string
  label: string
}

const AddExistingSource = ({
  sourcesJson,
  selectedSources,
  addOriginMenuOpen,
  closeAddOriginMenu,
  openAddOriginMenu,
  selectRefCallback,
  onCreateOriginClick,
  handleChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const filterOptions = useMemo(
    () => (candidate: { label: string; value: string }, input: string) => {
      if (selectedSources.includes(candidate.value)) {
        return false
      }

      const searchQueryLowerCase = input.toLowerCase()
      if (searchQueryLowerCase) {
        const source = sourcesJson[candidate.value]
        if (source.name.toLowerCase().includes(searchQueryLowerCase)) {
          return true
        }
        return false
      }

      return true
    },
    [sourcesJson, selectedSources],
  )

  const sourceOptions: SelectOptionProps[] = useMemo(
    () =>
      Object.keys(sourcesJson).map(sourceId => ({
        value: sourceId,
        label: sourcesJson[sourceId].name,
      })),
    [sourcesJson],
  )

  return (
    <VStack spacing="0" width="full" alignItems="start">
      <Box width="full">
        <IKReactSelect
          menuPlacement="auto"
          maxMenuHeight={220}
          refCallback={selectRefCallback}
          menuShouldScrollIntoView
          menuIsOpen={addOriginMenuOpen}
          onFocus={openAddOriginMenu}
          onBlur={closeAddOriginMenu}
          variant="compact"
          value={null}
          onChange={handleChange}
          options={sourceOptions}
          filterOption={filterOptions}
          placeholder={t('attach_existing_origin_cta', 'Attach an existing origin')}
        />
      </Box>
      <Link
        fontSize="0.825rem"
        onClick={onCreateOriginClick}
      >
        {t('create_new_origin', 'Create a new origin')}
      </Link>  
    </VStack>
  )
}

export default AddExistingSource
